/**
 * Uma conexão ao vivo (`text/event-stream`) que não morre calada (F212).
 *
 * O `EventSource` sozinho tem dois jeitos de parar para sempre sem ninguém
 * saber:
 *
 *   - **Fechado de vez.** Uma resposta 502 durante um deploy deixa o
 *     `readyState` em `CLOSED`, e o navegador não tenta mais.
 *   - **Aberto e mudo.** No desktop, depois de um deploy, a conexão ficava
 *     "aberta" sem nada chegar: em 29/09 o Atendimento de um computador passou
 *     das 09:56 às 10:37 sem receber mensagem, até alguém dar F5.
 *
 * Aqui: o servidor manda `event: ping` a cada 25 s; passados `idleMs` sem nada
 * (nem evento, nem ping), a conexão é reaberta. Fechada de vez, reabre com
 * espera crescente (5 s até 60 s). E toda reabertura depois da primeira avisa
 * `onResync`, porque o que chegou enquanto ela estava caída se perdeu.
 */

export type LiveStreamOptions = {
  readonly url: string
  readonly onMessage: (message: MessageEvent<string>) => void
  /** O servidor pediu, ou a conexão foi reaberta: recarregue o que mostra. */
  readonly onResync?: () => void
  readonly onOpen?: () => void
  /** Caiu; vai tentar de novo sozinha. Hora de ligar o plano B, se houver. */
  readonly onClosed?: () => void
  /** Silêncio máximo antes de reabrir. Três pings e um pouco. */
  readonly idleMs?: number
  /** Trocado nos testes. */
  readonly create?: (url: string) => EventSource
}

export const LIVE_STREAM_IDLE_MS = 70_000
const WATCH_EVERY_MS = 10_000
const FIRST_RETRY_MS = 5_000
const LAST_RETRY_MS = 60_000
/** `EventSource.CLOSED`, sem depender do global (que o teste não tem). */
const CLOSED = 2

export const openLiveStream = (
  options: LiveStreamOptions
): { close: () => void } => {
  const idleMs = options.idleMs ?? LIVE_STREAM_IDLE_MS
  const create = options.create ?? ((url: string) => new EventSource(url))

  let source: EventSource | null = null
  let lastActivity = Date.now()
  let opened = false
  let retryIn = FIRST_RETRY_MS
  let retry: ReturnType<typeof setTimeout> | null = null
  let stopped = false

  const touch = (): void => {
    lastActivity = Date.now()
  }

  const connect = (): void => {
    if (stopped) return
    touch()
    const current = create(options.url)
    source = current

    current.addEventListener('open', () => {
      touch()
      retryIn = FIRST_RETRY_MS
      if (opened) options.onResync?.()
      opened = true
      options.onOpen?.()
    })

    current.addEventListener('message', (message: MessageEvent<string>) => {
      touch()
      options.onMessage(message)
    })

    current.addEventListener('ping', touch)

    current.addEventListener('resync', () => {
      touch()
      options.onResync?.()
    })

    current.addEventListener('error', () => {
      if (current.readyState !== CLOSED) return
      if (source !== current) return
      options.onClosed?.()
      schedule()
    })
  }

  const drop = (): void => {
    source?.close()
    source = null
  }

  const schedule = (): void => {
    drop()
    if (stopped || retry !== null) return
    retry = setTimeout(() => {
      retry = null
      connect()
    }, retryIn)
    retryIn = Math.min(retryIn * 2, LAST_RETRY_MS)
  }

  const reopen = (): void => {
    if (stopped) return
    if (retry !== null) {
      clearTimeout(retry)
      retry = null
    }
    drop()
    connect()
  }

  const watchdog = setInterval(() => {
    if (source === null) return
    if (Date.now() - lastActivity >= idleMs) reopen()
  }, WATCH_EVERY_MS)

  // A internet voltou: não espera o cão de guarda.
  const online = (): void => {
    reopen()
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('online', online)
  }

  connect()

  return {
    close: () => {
      stopped = true
      clearInterval(watchdog)
      if (retry !== null) clearTimeout(retry)
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', online)
      }
      drop()
    },
  }
}
